/**
 * Onboarding API Service
 *
 * SESSION_199: Step 1 - Business info editing
 * SESSION_200: Step 2 - Contact preferences
 *
 * Handles HTTP requests for user onboarding flow.
 * Backend endpoints created in SESSION_198 and SESSION_200.
 */

import { apiClient } from './ApiClient';
import type {
  OnboardingStatus,
  UpdateBusinessByOwnerRequest,
  CompleteOnboardingResponse,
  UpdateContactPreferencesRequest,
  SubmitAddressCorrectionRequest,
  SubmitAddressCorrectionResponse
} from '../../domain/entities/Onboarding';

/**
 * API service for user onboarding
 */
export class OnboardingApi {
  /**
   * Get user's onboarding status and business info
   * Endpoint: GET /api/users/onboarding-status
   *
   * Returns whether onboarding is complete and associated business info
   */
  async getOnboardingStatus(): Promise<OnboardingStatus> {
    return apiClient.get<OnboardingStatus>('/api/users/onboarding-status');
  }

  /**
   * Mark user's onboarding as complete
   * Endpoint: POST /api/users/complete-onboarding
   *
   * Sets HasCompletedOnboarding = true for the current user
   */
  async completeOnboarding(): Promise<CompleteOnboardingResponse> {
    return apiClient.post<CompleteOnboardingResponse>('/api/users/complete-onboarding');
  }

  /**
   * Update business information by owner
   * Endpoint: PUT /api/businesses/{id}/owner-update
   *
   * Allows business owners to update: phone, email, website, description
   * Name and address are NOT editable (use address correction flow)
   * @param businessId - Business ID to update
   * @param request - Update request with optional fields
   */
  async updateBusinessByOwner(
    businessId: string,
    request: UpdateBusinessByOwnerRequest
  ): Promise<void> {
    return apiClient.put<void>(`/api/businesses/${businessId}/owner-update`, request);
  }

  /**
   * Update user's contact/notification preferences
   * Endpoint: PUT /api/users/contact-preferences
   * SESSION_200: Onboarding Step 2 - Contact Preferences
   *
   * @param request - Contact preferences to update
   */
  async updateContactPreferences(request: UpdateContactPreferencesRequest): Promise<void> {
    return apiClient.put<void>('/api/users/contact-preferences', request);
  }

  /**
   * Submit an address correction request
   * Endpoint: POST /api/address-corrections
   * SESSION_201: Address correction feature
   *
   * @param request - The address correction request
   */
  async submitAddressCorrection(request: SubmitAddressCorrectionRequest): Promise<SubmitAddressCorrectionResponse> {
    return apiClient.post<SubmitAddressCorrectionResponse>('/api/address-corrections', request);
  }

  /**
   * Update business details on a pending business claim
   * Endpoint: PUT /api/business-claims/{claimId}/business-details
   * SESSION_339: Enable full onboarding for pending claims
   *
   * Allows users to update phone, email, website, description during onboarding
   * for new business claims that are pending admin approval.
   *
   * @param claimId - The business claim ID to update
   * @param request - Update request with business details
   */
  async updateClaimBusinessDetails(
    claimId: string,
    request: UpdateBusinessByOwnerRequest
  ): Promise<void> {
    return apiClient.put<void>(`/api/business-claims/${claimId}/business-details`, request);
  }
}

// Export singleton instance
export const onboardingApi = new OnboardingApi();
